import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft,
  Check,
  Heart,
  Minimize2,
  MoreHorizontal,
  Pause,
  Play,
  Sparkles,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { API_BASE } from '../../config';
import type { FeaturedRelatedVideo, VideoSource } from '../../config/featuredTemplates';

/* 秒 → m:ss（超过 99 分钟按累计分钟显示，对齐参考图 17:22 格式） */
const fmt = (t: number) => {
  const safe = Number.isFinite(t) && t > 0 ? t : 0;
  const m = Math.floor(safe / 60);
  const s = Math.floor(safe % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

/* 画质弹窗降级列表（无多档源数据时展示，1080P→360P 降序）；用户作品有多档源
   （后端异步转码生成）时以实际 sources 档位为准 */
const QUALITIES = ['1080P', '720P', '480P', '360P'] as const;

/**
 * 全屏视频播放器（参考图一/图二：全屏完整控制态 → 5 秒无操作隐藏控制纯画面态）：
 * - fixed 满屏覆盖层（createPortal 挂 body：预览页根容器 z-[160] 自建堆叠上下文，
 *   困在其中的 z-[300] 永远压不过根层的侧边栏/顶栏 z-[200]，portal 到 body 后
 *   在根层以 z-[300] 参与竞争才能盖住边栏），视频 object-contain 满铺
 * - 控制组件（参考图一）：左上返回、顶部居中缩略图条（当前白描边）、
 *   底部信息层（标题 + 内容由 AI 生成 + 作者 + 关注 | 查看制作流程 + 点赞 + 更多）、
 *   底部控制条（播放/暂停 + 时间 + 可点进度条 + 音量 + 画质 + 退出全屏）
 * - 5 秒无指针操作隐藏全部控制并隐藏光标（参考图二），任何指针活动立即恢复
 * - 打开时从预览页暂停进度继续播放（点击手势上下文，允许出声）；
 *   点缩略图切换视频从头播；Esc / 返回 / 退出全屏按钮均可关闭
 */
export const FullscreenVideoPlayer: React.FC<{
  playlist: FeaturedRelatedVideo[];
  activeIdx: number;
  /** 打开时续播的起始秒数（取自预览页视频当前进度，仅首个视频生效） */
  startTime: number;
  title: string;
  author: string;
  /** 与 FeaturedPreviewItem.likes 同源（string，直接渲染） */
  likes: string;
  onSelect: (index: number) => void;
  /** 「查看制作流程」入口（无快照作品不传则按钮不渲染） */
  onOpenWorkflow?: () => void;
  onClose: () => void;
}> = ({ playlist, activeIdx, startTime, title, author, likes, onSelect, onOpenWorkflow, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  /* 画质：有多档源（sources）时选档即真实切源——记录当前进度 → 换 src →
     loadedmetadata 后 seek 回原进度并恢复播放状态（无缝切换）；无 sources
     且为 /assets/ 源时按 url 懒查询档位清单；静态模板视频仅更新档位标识 */
  const [quality, setQuality] = useState<string>('1080P');
  const [qualityOpen, setQualityOpen] = useState(false);
  /* 懒查询到的档位清单（按视频 id 缓存）：发布响应时转码尚未完成，sources 常为空 */
  const [fetchedSources, setFetchedSources] = useState<Record<string, VideoSource[]>>({});
  /* 切档续播挂起状态：换 src 后等 loadedmetadata 再回进度、恢复播放 */
  const pendingSeekRef = useRef<number | null>(null);
  const resumePlayRef = useRef(false);
  const [followed, setFollowed] = useState(false);
  /* 控制层可见性：5 秒无指针操作隐藏（图二态），指针活动恢复（图一态） */
  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimerRef = useRef(0);
  /* startTime 仅首个视频 seek 一次：点缩略图切换后从头播放，不再回到续播点 */
  const seekedRef = useRef(false);
  const seekBarRef = useRef<HTMLDivElement>(null);

  const active = playlist[activeIdx] || playlist[0];

  /* 生效档位清单：playlist 自带优先，否则用懒查询结果 */
  const activeSources = active.sources?.length ? active.sources : fetchedSources[active.id];

  /* 菜单档位固定 1080P→360P 降序：sources 返回顺序不保证（转码写入顺序），
     按档位数值降序排，兜底列表与真实 sources 走同一顺序 */
  const qualityOptions = useMemo(
    () =>
      activeSources?.length
        ? [...activeSources].sort((a, b) => parseInt(b.label, 10) - parseInt(a.label, 10)).map((s) => s.label)
        : [...QUALITIES],
    [activeSources]
  );

  /* 打开/切换视频时懒查档位：仅 /assets/ 源（用户上传/生成作品经后端转码）；
     拉到后 activeSources 更新 → 下方 effect 同步档位标识与菜单列表 */
  useEffect(() => {
    const src = active.videoUrl || '';
    if (activeSources || !src.startsWith('/assets/')) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/assets/video-sources?url=${encodeURIComponent(src)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data?.video_sources?.length) {
          setFetchedSources((prev) => ({ ...prev, [active.id]: data.video_sources }));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [active.id, activeSources, active.videoUrl]);

  /* 档位标识跟随实际源：多档时取最高档 label（不依赖 sources 返回顺序）；
     单源（模板视频/转码未完成）保持 1080P 默认 */
  useEffect(() => {
    setQuality(qualityOptions[0] || '1080P');
  }, [qualityOptions]);

  /* 挂载即播 + 续播进度；点击缩略图切换视频（key 重建 video）后从头自动播放 */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    /* 切换视频：清掉上一条遗留的切档挂起状态，避免旧进度被 seek 到新视频上 */
    pendingSeekRef.current = null;
    resumePlayRef.current = false;
    if (!seekedRef.current) {
      seekedRef.current = true;
      if (startTime > 0) video.currentTime = startTime;
    }
    video.play().catch(() => setIsPlaying(false));
  }, [activeIdx, startTime]);

  /* 5 秒无操作隐藏控制：任意指针活动（移动/按下）立即恢复并重置计时 */
  const armHideTimer = useCallback(() => {
    setControlsVisible(true);
    window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => {
      /* 控制层整体隐藏时同步收起画质菜单，避免恢复时残留展开态 */
      setQualityOpen(false);
      setControlsVisible(false);
    }, 5000);
  }, []);
  useEffect(() => {
    armHideTimer();
    return () => window.clearTimeout(hideTimerRef.current);
  }, [armHideTimer]);

  /* Esc 关闭（覆盖层惯例，与返回/退出全屏按钮等效） */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        /* 画质菜单打开时 Esc 只关菜单，再按才退出全屏 */
        if (qualityOpen) {
          setQualityOpen(false);
          return;
        }
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, qualityOpen]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  /* 点进度条 seek：按点击位置比例换算到时长 */
  const handleSeek = (e: React.MouseEvent) => {
    const bar = seekBarRef.current;
    const video = videoRef.current;
    if (!bar || !video || !duration) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    video.currentTime = ratio * duration;
    setProgress(ratio * duration);
  };

  /* 选档：有多档源时真实切源（记录进度 → 换 src → loadedmetadata 后 seek 续播），
     无档位数据（模板静态视频）仅更新标识 */
  const handleQualitySelect = (label: string) => {
    setQualityOpen(false);
    const video = videoRef.current;
    const target = activeSources?.find((s) => s.label === label);
    /* 用 getAttribute 比较原始 src：video.src 属性返回带 origin 的绝对地址，与相对路径恒不等 */
    if (!video || !target || target.url === video.getAttribute('src')) return;
    pendingSeekRef.current = video.currentTime;
    resumePlayRef.current = !video.paused;
    video.pause();
    setQuality(label);
    video.src = target.url;
    video.load();
  };

  /* 控制层统一过渡：可见时 opacity-100，隐藏时淡出并禁用指针（光标由容器 cursor-none 隐藏） */
  const fade = controlsVisible ? 'opacity-100' : 'pointer-events-none opacity-0';

  return createPortal(
    <div
      className={`fixed inset-0 z-[300] bg-black ${controlsVisible ? '' : 'cursor-none'}`}
      onMouseMove={armHideTimer}
      onMouseDown={() => {
        armHideTimer();
        /* 点弹窗外部任意处关闭画质菜单（菜单内部 onMouseDown 阻断冒泡） */
        setQualityOpen(false);
      }}
    >
      <video
        key={active.id}
        ref={videoRef}
        src={active.videoUrl}
        poster={active.cover || undefined}
        autoPlay
        loop
        playsInline
        onClick={togglePlay}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          setDuration(e.currentTarget.duration || 0);
          /* 切档元数据就绪：回到切档前进度并恢复原播放状态（无缝切换的关键一跳；
             显式 play/pause 双向覆盖——换 src + load 后带 autoPlay 属性可能自动开播） */
          const pending = pendingSeekRef.current;
          if (pending != null) {
            pendingSeekRef.current = null;
            e.currentTarget.currentTime = pending;
            if (resumePlayRef.current) e.currentTarget.play().catch(() => {});
            else e.currentTarget.pause();
            resumePlayRef.current = false;
          }
        }}
        /* object-contain 完整显示不裁切，比例差由黑边吸收（与预览页同哲学） */
        className="h-full w-full object-contain"
      />

      {/* 底部渐变衬底：信息层/控制条可读性（图一底部暗部） */}
      <div
        className={`pointer-events-none absolute inset-x-0 bottom-0 h-[220px] bg-gradient-to-t from-black/85 via-black/40 to-transparent transition-opacity duration-300 ${fade}`}
      />

      {/* 左上返回：退出全屏回预览页 */}
      <button
        type="button"
        onClick={onClose}
        aria-label="退出全屏"
        className={`absolute left-6 top-4 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-opacity duration-300 hover:bg-white/10 ${fade}`}
      >
        <ArrowLeft size={26} className="text-white" />
      </button>

      {/* 顶部居中缩略图条：点选切换播放视频（当前白描边 2，未选压暗悬停提亮） */}
      <div
        className={`absolute left-1/2 top-3 z-10 flex max-w-[70vw] -translate-x-1/2 items-center gap-2 overflow-x-auto no-scrollbar rounded-xl p-1 transition-opacity duration-300 ${fade}`}
      >
        {playlist.map((v, i) => (
          <button
            key={v.id}
            type="button"
            onClick={() => onSelect(i)}
            aria-label={v.title}
            className={`relative h-[54px] w-[88px] flex-shrink-0 cursor-pointer overflow-hidden rounded-lg bg-[#2D2D2D] transition-opacity ${
              i === activeIdx
                ? 'ring-2 ring-white'
                : 'opacity-75 hover:opacity-100'
            }`}
          >
            {v.cover ? (
              <img src={v.cover} alt={v.title} className="h-full w-full object-cover" />
            ) : (
              <video
                src={`${v.videoUrl}#t=0.1`}
                preload="metadata"
                muted
                playsInline
                aria-hidden
                tabIndex={-1}
                className="pointer-events-none h-full w-full object-cover"
              />
            )}
          </button>
        ))}
      </div>

      {/* 底部信息层（控制条上方）：左 = 标题 + AI 声明 / 作者 + 关注；右 = 制作流程 / 点赞 / 更多 */}
      <div
        className={`absolute inset-x-0 bottom-[60px] z-10 flex items-end justify-between gap-6 px-8 transition-opacity duration-300 ${fade}`}
      >
        <div className="min-w-0">
          <div className="flex items-baseline gap-3">
            <h1 className="truncate text-[24px] font-semibold leading-[32px] text-white">《{title}》</h1>
            <span className="flex-shrink-0 text-[12px] leading-[16px] text-white/50">内容由 AI 生成</span>
          </div>
          <div className="mt-2 flex items-center">
            <img
              src="/images/home/avatars/avatar-user.png"
              alt=""
              className="h-6 w-6 rounded-full object-cover"
            />
            <span className="ml-2 text-[14px] leading-[14px] text-white">{author}</span>
            <button
              type="button"
              onClick={() => setFollowed((f) => !f)}
              className={`ml-3 h-7 cursor-pointer rounded-lg px-3 text-[13px] leading-[18px] transition-colors ${
                followed
                  ? 'bg-white/10 text-white/50 hover:bg-white/15'
                  : 'bg-white/15 text-white hover:bg-white/25'
              }`}
            >
              {followed ? '已关注' : '关注'}
            </button>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {onOpenWorkflow && (
            <button
              type="button"
              onClick={onOpenWorkflow}
              className="flex h-9 cursor-pointer items-center gap-1.5 rounded-lg bg-white/10 px-3 text-[13px] text-white transition-colors hover:bg-white/20"
            >
              <Sparkles size={16} />
              查看制作流程
            </button>
          )}
          <button
            type="button"
            className="flex h-9 cursor-pointer items-center gap-1.5 rounded-lg bg-white/10 px-3 text-[13px] text-white transition-colors hover:bg-white/20"
          >
            <Heart size={16} fill="white" />
            {likes}
          </button>
          <button
            type="button"
            aria-label="更多"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <MoreHorizontal size={18} />
          </button>
        </div>
      </div>

      {/* 底部控制条：播放/暂停 + 时间 / 进度条 / 音量 + 画质 + 退出全屏（参考图一） */}
      <div
        className={`absolute inset-x-0 bottom-0 z-10 flex h-[52px] items-center gap-4 px-8 transition-opacity duration-300 ${fade}`}
      >
        <button
          type="button"
          onClick={togglePlay}
          aria-label={isPlaying ? '暂停' : '播放'}
          className="flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center text-white transition-colors hover:text-white/80"
        >
          {isPlaying ? <Pause size={20} /> : <Play size={20} />}
        </button>
        <span className="flex-shrink-0 text-[13px] leading-[16px] text-white">
          {fmt(progress)}
          <span className="text-white/60"> / {fmt(duration)}</span>
        </span>
        {/* 进度条：整体可点 seek，已播白色 / 剩余白 30%，悬停加粗 */}
        <div
          ref={seekBarRef}
          onClick={handleSeek}
          className="group relative h-[3px] min-w-0 flex-1 cursor-pointer rounded-full bg-white/30 transition-[height] duration-150 hover:h-[5px]"
        >
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-white"
            style={{ width: `${duration ? Math.min(100, (progress / duration) * 100) : 0}%` }}
          />
        </div>
        <button
          type="button"
          onClick={toggleMute}
          aria-label={isMuted ? '取消静音' : '静音'}
          className="flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center text-white transition-colors hover:text-white/80"
        >
          {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
        {/* 画质：点击弹出档位菜单（参考图：深色圆角面板、当前项右侧白勾） */}
        <div className="relative flex-shrink-0">
          <button
            type="button"
            onClick={() => setQualityOpen((o) => !o)}
            aria-label="选择画质"
            className="cursor-pointer text-[13px] leading-[16px] text-white transition-colors hover:text-white/80"
          >
            {quality}
          </button>
          {/* onMouseDown 阻断冒泡：点菜单内部不触发根层「点外关闭」；菜单位于
              控制条容器内，控制层 5 秒隐藏时随 fade 一起淡出 */}
          {qualityOpen && (
            <div
              onMouseDown={(e) => e.stopPropagation()}
              className="absolute bottom-full right-0 z-20 mb-3 w-[108px] overflow-hidden rounded-xl bg-[#1D1D1D] py-1 shadow-[0_4px_16px_rgba(0,0,0,0.5)]"
            >
              {qualityOptions.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => handleQualitySelect(q)}
                  className={`flex h-10 w-full cursor-pointer items-center justify-between px-4 text-[14px] leading-[20px] transition-colors ${
                    q === quality
                      ? 'text-white'
                      : 'text-white/90 hover:bg-white/10'
                  }`}
                >
                  {q}
                  {q === quality && <Check size={16} />}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="退出全屏"
          className="flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center text-white transition-colors hover:text-white/80"
        >
          <Minimize2 size={20} />
        </button>
      </div>
    </div>,
    document.body
  );
};
